import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import CircularProgress from '@mui/material/CircularProgress';
import AuthenticatedRoute from './AuthenticatedRoute';
import UnauthenticatedRoute from './UnauthenticatedRoute';
import DefaultRoute from './DefaultRoute';

const Login = lazy(
  () => import(/* webpackChunkName: "LoginPage" */ '../pages/Login')
);
const Users = lazy(
  () => import(/* webpackChunkName: "UsersPage" */ '../pages/Users')
);
const Chapters = lazy(
  () => import(/* webpackChunkName: "ChaptersPage" */ '../pages/Chapters')
);
const ChaptersCreate = lazy(
  () =>
    import(
      /* webpackChunkName: "ChaptersCreatePage" */ '../pages/Chapters/Create'
    )
);
const ChaptersEdit = lazy(
  () =>
    import(/* webpackChunkName: "ChaptersEditPage" */ '../pages/Chapters/Edit')
);
const StreamingPlatforms = lazy(
  () =>
    import(
      /* webpackChunkName: "StreamingPlatformsPage" */ '../pages/StreamingPlatforms'
    )
);
const StreamingPlatformsCreate = lazy(
  () =>
    import(
      /* webpackChunkName: "StreamingPlatformsCreatePage" */ '../pages/StreamingPlatforms/Create'
    )
);
const StreamingPlatformsEdit = lazy(
  () =>
    import(
      /* webpackChunkName: "StreamingPlatformsEditPage" */ '../pages/StreamingPlatforms/Edit'
    )
);
const Lessons = lazy(
  () => import(/* webpackChunkName: "LessonsPage" */ '../pages/Lessons')
);
const Surprise = lazy(
  () => import(/* webpackChunkName: "SurprisePage" */ '../pages/Surprise')
);
const LessonsCreate = lazy(
  () =>
    import(
      /* webpackChunkName: "LessonsCreatePage" */ '../pages/Lessons/Create'
    )
);
const SurpriseCreate = lazy(
  () =>
    import(
      /* webpackChunkName: "SurpriseCreatePage" */ '../pages/Surprise/Create'
    )
);
const LessonsEdit = lazy(
  () =>
    import(/* webpackChunkName: "LessonsEditPage" */ '../pages/Lessons/Edit')
);
const SurpriseEdit = lazy(
  () =>
    import(/* webpackChunkName: "SurpriseEditPage" */ '../pages/Surprise/Edit')
);
const Videos = lazy(
  () => import(/* webpackChunkName: "VideosPage" */ '../pages/Videos')
);
const VideosCreate = lazy(
  () =>
    import(/* webpackChunkName: "VideosCreatePage" */ '../pages/Videos/Create')
);
const VideosEdit = lazy(
  () => import(/* webpackChunkName: "VideosEditPage" */ '../pages/Videos/Edit')
);
const Filmes = lazy(
  () => import(/* webpackChunkName: "FilmesPage" */ '../pages/Filmes')
);
const FilmesEdit = lazy(
  () => import(/* webpackChunkName: "FilmesEditPage" */ '../pages/Filmes/Edit')
);
const Events = lazy(
  () => import(/* webpackChunkName: "EventsPage" */ '../pages/Events')
);
const EventsEdit = lazy(
  () => import(/* webpackChunkName: "EventsEditPage" */ '../pages/Events/Edit')
);
const ComplementaryEvents = lazy(
  () =>
    import(
      /* webpackChunkName: "ComplementaryEventsPage" */ '../pages/ComplementaryEvents'
    )
);
const ComplementaryEventsEdit = lazy(
  () =>
    import(
      /* webpackChunkName: "ComplementaryEventsEditPage" */ '../pages/ComplementaryEvents/Edit'
    )
);

const Genres = lazy(
  () => import(/* webpackChunkName: "GenresPage" */ '../pages/Genres')
);
const Configs = lazy(
  () => import(/* webpackChunkName: "ConfigsPage" */ '../pages/Configs')
);

const Discounts = lazy(
  () => import(/* webpackChunkName: "ConfigsPage" */ '../pages/Discounts')
);

const Wallets = lazy(
  () => import(/* webpackChunkName: "ConfigsPage" */ '../pages/Wallets')
);

const Transactions = lazy(
  () => import(/* webpackChunkName: "ConfigsPage" */ '../pages/Transactions')
);
const Quizzes = lazy(
  () => import(/* webpackChunkName: "QuizzesPage" */ '../pages/Quizzes')
);
const QuizzesCreate = lazy(
  () =>
    import(
      /* webpackChunkName: "QuizzesCreatePage" */ '../pages/Quizzes/Create'
    )
);
const QuizzesEdit = lazy(
  () =>
    import(/* webpackChunkName: "QuizzesEditPage" */ '../pages/Quizzes/Edit')
);
const QuizResults = lazy(
  () => import(/* webpackChunkName: "QuizResultsPage" */ '../pages/QuizResults')
);

export default function index() {
  const useStyles = makeStyles(() => ({
    center: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100vw',
      height: '100vh',
    },
  }));

  const classes = useStyles();
  return (
    <Suspense
      fallback={
        <div className={classes.center}>
          <CircularProgress />
        </div>
      }
    >
      <Routes>
        <Route
          path="/login"
          element={
            <UnauthenticatedRoute>
              <Login />
            </UnauthenticatedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AuthenticatedRoute>
              <Users />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/chapters"
          element={
            <AuthenticatedRoute>
              <Chapters />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/chapters/create"
          element={
            <AuthenticatedRoute>
              <ChaptersCreate />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/chapters/edit/:id"
          element={
            <AuthenticatedRoute>
              <ChaptersEdit />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/lessons"
          element={
            <AuthenticatedRoute>
              <Lessons />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/surprise"
          element={
            <AuthenticatedRoute>
              <Surprise />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/lessons/create"
          element={
            <AuthenticatedRoute>
              <LessonsCreate />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/surprise/create"
          element={
            <AuthenticatedRoute>
              <SurpriseCreate />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/lessons/edit/:id"
          element={
            <AuthenticatedRoute>
              <LessonsEdit />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/surprise/edit/:id"
          element={
            <AuthenticatedRoute>
              <SurpriseEdit />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/videos"
          element={
            <AuthenticatedRoute>
              <Videos />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/videos/create"
          element={
            <AuthenticatedRoute>
              <VideosCreate />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/videos/edit/:id"
          element={
            <AuthenticatedRoute>
              <VideosEdit />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/streaming-platforms"
          element={
            <AuthenticatedRoute>
              <StreamingPlatforms />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/streaming-platforms/create"
          element={
            <AuthenticatedRoute>
              <StreamingPlatformsCreate />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/streaming-platforms/edit/:id"
          element={
            <AuthenticatedRoute>
              <StreamingPlatformsEdit />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/films"
          element={
            <AuthenticatedRoute>
              <Filmes />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/films/edit/:id"
          element={
            <AuthenticatedRoute>
              <FilmesEdit />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/events"
          element={
            <AuthenticatedRoute>
              <Events />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/events/edit/:id"
          element={
            <AuthenticatedRoute>
              <EventsEdit />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/complementary-events"
          element={
            <AuthenticatedRoute>
              <ComplementaryEvents />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/complementary-events/edit/:id"
          element={
            <AuthenticatedRoute>
              <ComplementaryEventsEdit />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/wallets"
          element={
            <AuthenticatedRoute>
              <Wallets />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/transactions"
          element={
            <AuthenticatedRoute>
              <Transactions />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/genres"
          element={
            <AuthenticatedRoute>
              <Genres />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/configs"
          element={
            <AuthenticatedRoute>
              <Configs />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/discounts"
          element={
            <AuthenticatedRoute>
              <Discounts />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/quizzes"
          element={
            <AuthenticatedRoute>
              <Quizzes />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/quizzes/create"
          element={
            <AuthenticatedRoute>
              <QuizzesCreate />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/quizzes/edit/:id"
          element={
            <AuthenticatedRoute>
              <QuizzesEdit />
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/admin/quiz-results"
          element={
            <AuthenticatedRoute>
              <QuizResults />
            </AuthenticatedRoute>
          }
        />
        <Route path="*" element={<DefaultRoute />} />
      </Routes>
    </Suspense>
  );
}
